import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { AppConstants } from '../constants/app.contants';
import { environment } from 'src/environments/environment';
import { ToasterService } from './toaster.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private isEndingSession = false;
  private refreshInFlight$?: Observable<boolean>;
  private refreshTimer?: number;

  constructor(
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly toasterService: ToasterService,
    private readonly injector: Injector
  ) {}

  refreshSession(): Observable<boolean> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const accessToken = this.userService.token || localStorage.getItem('QNBNToken') || '';
    const refreshToken = this.userService.refreshToken || localStorage.getItem('QNBNRefreshToken') || '';
    if (!accessToken || !refreshToken) {
      return of(false);
    }

    const headers = new HttpHeaders({ 'X-Skip-Session-Refresh': 'true', 'X-Skip-Retry': 'true' });
    this.refreshInFlight$ = this.injector.get(HttpClient).post<any>(
      `${environment.API_URL}/${environment.APIs_PREFIX}/User/RefreshToken`,
      { accessToken, refreshToken },
      { headers }
    ).pipe(
      map(response => {
        if (response?.isSuccess === true && response?.data?.token) {
          this.userService.handleValidAuthentication(response);
          this.startAutoRefresh();
          return true;
        }
        return false;
      }),
      catchError(() => of(false)),
      finalize(() => this.refreshInFlight$ = undefined),
      shareReplay(1)
    );

    return this.refreshInFlight$;
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.refreshTimer = window.setInterval(() => {
      this.refreshSession().subscribe(refreshed => {
        if (!refreshed) {
          this.endSession();
        }
      });
    }, 4 * 60 * 60 * 1000);
  }

  stopAutoRefresh(): void {
    if (this.refreshTimer !== undefined) {
      window.clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  endSession(): void {
    if (this.isEndingSession) {
      return;
    }

    this.isEndingSession = true;
    this.stopAutoRefresh();
    this.userService.logout();
    this.toasterService.showWarning('Your session has expired. Please log in again.', 'Session Expired');

    this.router.navigate([AppConstants.ROUTES.AUTHENTICATION, AppConstants.ROUTES.LOGIN])
      .finally(() => {
        setTimeout(() => this.isEndingSession = false, 500);
      });
  }
}
